import {
  Badge,
  Banner,
  FlexBox,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { Tag } from 'lib-shared-types-api';
import { useCallback, useMemo, useState } from 'react';

import { searchApi } from 'apis/search';
import {
  BulkModalAction,
  conversationsBulkModalActions,
} from 'app/components/appModals/components/conversationsBulkModal';
import { ConversationsBulkTextEditor } from 'components/conversation/conversationsBulkTextEditor';
import { TagSelect } from 'components/tagSelect';
import { createAppModal } from 'utils/modal';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';

import { createLinkInternalNote } from '../../utils/linkInternalNote';

import styles from './conversationLinkModal.module.scss';

export const ConversationLinkModal = createAppModal(
  { type: 'app:conversationLink' },
  ({ isOpen, modal, hide, meta }) => {
    const { csids, parent_csid } = modal.arg;
    const organizationId = useAppSelector(
      (state) => state.settings.organization.organization.id,
    );
    const [text, setText] = useState('');
    const [tags, setTags] = useState<readonly Tag[]>([]);
    const dispatch = useAppDispatch();

    const { data: linkedConversations, isFetching } =
      searchApi.useReadSearchConversationQuery({
        limit: 100,
        filter: {
          facets: {
            linked_to: csids.map((csid) => `${csid}_${organizationId}`),
          },
        },
      });

    const [eligibleConversationIds, ineligibleConversationIds] = useMemo(() => {
      if (isFetching) {
        return [[], []];
      }

      const { hits } = linkedConversations?.data.results ?? {};
      if (!hits?.length) {
        return [csids, []];
      }

      const csidsWithChildren = new Set(
        hits.map(({ linked_to }) => linked_to?.split('_').at(0)),
      );

      return [
        csids.filter((csid) => !csidsWithChildren.has(csid.toString())),
        Array.from(csidsWithChildren),
      ];
    }, [csids, isFetching, linkedConversations]);

    const onLink = useCallback(async () => {
      const noteCsids = [parent_csid, ...eligibleConversationIds];
      const actions: BulkModalAction[] = [
        {
          type: 'close',
          isValid: true,
          arg: { csids: eligibleConversationIds },
        },
        {
          type: 'link',
          isValid: true,
          arg: {
            csids: eligibleConversationIds,
            link_type: 'related',
            parent_csid,
          },
        },
        {
          type: 'add_internal_note',
          isValid: true,
          arg: {
            csids: noteCsids,
            text: createLinkInternalNote(text, noteCsids),
          },
        },
      ];

      if (tags.length) {
        actions.push({
          type: 'add_tag',
          isValid: true,
          arg: {
            action: 'add_tag',
            csids: eligibleConversationIds,
            names: tags.map(({ name }) => name),
          },
        });
      }

      dispatch(
        conversationsBulkModalActions.initBulkUpdate({
          waitTime: 0,
          actions,
        }),
      );

      meta?.resolve({ isLinkInitiated: true });

      hide();

      ampli.linkConversationsSubmitted();
    }, [
      parent_csid,
      text,
      tags,
      eligibleConversationIds,
      meta,
      dispatch,
      hide,
    ]);

    const handleClose = useCallback(() => {
      meta?.resolve({ isLinkInitiated: false });
      hide();
    }, [hide, meta]);

    return (
      <Modal isOpen={isOpen} onClose={handleClose} className={styles.root}>
        <ModalHead title="Link conversations" onClose={handleClose} />

        <ModalBody>
          <FlexBox flexDirection="column" gap="200">
            <Text.Paragraph subdued>
              Click the “Link” button to link selected conversations with the
              current one and automatically close, so you can focus on the
              current conversation. You can add additional information to the
              automatically generated internal notes below.
            </Text.Paragraph>

            {ineligibleConversationIds.length > 0 && (
              <Banner
                variant="fail"
                headline="Validation error"
                description={
                  <Text.Paragraph subdued as="span">
                    The following conversations already have conversations
                    linked to them and were excluded from the linking:{' '}
                    {ineligibleConversationIds.join(', ')}
                  </Text.Paragraph>
                }
              />
            )}

            <Text.Paragraph variant="3" subdued>
              Additional internal note
            </Text.Paragraph>
            <ConversationsBulkTextEditor
              type="add_internal_note"
              placeholder="Add additional information here"
              onChange={setText}
            />

            <Text.Paragraph variant="3" subdued>
              Additional tags
            </Text.Paragraph>
            <TagSelect
              allowMultiple
              allowTagCreation
              onChange={setTags}
              placeholder="Select one or more tags to add"
            />

            {eligibleConversationIds.length ? (
              <>
                <Text.Paragraph variant="3" subdued>
                  Conversations that you&apos;re about to link:
                </Text.Paragraph>
                <FlexBox flexWrap="wrap" gap="100">
                  {eligibleConversationIds.map((csid) => (
                    <Badge key={csid} size="medium" variant="info">
                      #{csid}
                    </Badge>
                  ))}
                </FlexBox>
                <Text.Paragraph variant="3" subdued>
                  with:
                </Text.Paragraph>
                <FlexBox>
                  <Badge size="medium" variant="primary">
                    #{parent_csid}
                  </Badge>
                </FlexBox>
              </>
            ) : null}
          </FlexBox>
        </ModalBody>

        <ModalFoot
          confirmProps={{
            isLoading: isFetching,
            disabled: !eligibleConversationIds.length,
          }}
          confirmOnClick={onLink}
          cancelOnClick={handleClose}
          confirmName="Link"
        />
      </Modal>
    );
  },
);
